import { useQContent } from '@exam-management/common/qcontent-provider';
import { QuestionStatus } from '@exam-management/graphql';
import { useQBankStructure } from '@exam-management/qbs-provider';
import {
  type QContentDetailFormContextData,
  getPreDraftSingleQuestionSchema,
  getQContentCreateSchema,
} from '@exam-management/qcontent-management';
import { useParseContentForQuestion } from '@exam-management/rich-text-editor';
import { useParams } from '@exam-management/router';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loading } from '@shared/components';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import type { QContentDetailFormProviderProps } from './types';
import { getQContentEditDefaultValues } from './utils';

export default function QContentDetailFormProvider(
  props: Readonly<QContentDetailFormProviderProps>,
) {
  const { children } = props;
  const { contentId } = useParams('/qbank-structures/:structureId/qcontents/single/:contentId');
  const { qBankStructureLevelItems, qBankStructureLevels } = useQBankStructure();
  const { qContent: question } = useQContent();
  const [isLoading, setIsLoading] = useState(true);

  const QContentEditSchema = getQContentCreateSchema(qBankStructureLevels);
  const QContentPreDraftSchema = getPreDraftSingleQuestionSchema(qBankStructureLevels);

  const formContext = useForm<QContentDetailFormContextData>({
    resolver: zodResolver(
      question.status === QuestionStatus.PreDraft ? QContentPreDraftSchema : QContentEditSchema,
    ),
  });

  const { parseContentForQuestion } = useParseContentForQuestion({
    questionId: contentId,
  });

  const handleResetForm = useCallback(async () => {
    const defaultValues = await getQContentEditDefaultValues(
      question,
      qBankStructureLevels,
      qBankStructureLevelItems,
      parseContentForQuestion,
    );

    formContext.reset(defaultValues as Partial<QContentDetailFormContextData>);

    setIsLoading(false);
  }, [question]);

  useEffect(() => {
    handleResetForm();
  }, [handleResetForm]);

  if (isLoading) {
    return (
      <Loading
        sx={{
          height: '100dvh',
        }}
      />
    );
  }

  return <FormProvider {...formContext}>{children}</FormProvider>;
}
